import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Save, X, Package } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";

const EMPTY = { sku: "", name: "", unit: "pcs", price: "", gst_percent: "18", hsn_code: "" };
const UNIT_OPTIONS = ["pcs", "sqft", "meter", "kg", "box", "set", "roll"];

export default function ProductsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/products");
      setItems(data);
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({
      sku: p.sku, name: p.name, unit: p.unit,
      price: String(p.price), gst_percent: String(p.gst_percent),
      hsn_code: p.hsn_code || "",
    });
  };
  const cancelEdit = () => { setEditingId(null); setForm(EMPTY); };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      sku: form.sku.trim(),
      name: form.name.trim(),
      unit: form.unit,
      price: parseFloat(form.price) || 0,
      gst_percent: parseFloat(form.gst_percent) || 0,
      hsn_code: form.hsn_code.trim() || null,
    };
    if (!payload.sku || !payload.name) { toast.error("SKU and Name are required"); return; }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/admin/products/${editingId}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Product added");
      }
      cancelEdit();
      refresh();
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}" (${p.sku})?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Deleted");
      refresh();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="p-6 md:p-8" data-testid="admin-products-panel">
      <form onSubmit={save} className="bg-stone-50 rounded-2xl p-5 grid grid-cols-2 md:grid-cols-7 gap-3 items-end mb-8">
        <Field label="SKU *" testId="prod-sku" value={form.sku} onChange={(v) => setForm({ ...form, sku: v })} />
        <div className="col-span-2 md:col-span-2">
          <Label>Name *</Label>
          <input data-testid="prod-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
        </div>
        <div>
          <Label>Unit</Label>
          <select data-testid="prod-unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className={inputCls}>
            {UNIT_OPTIONS.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>
        <Field label="Price (₹) *" testId="prod-price" type="number" value={form.price} onChange={(v) => setForm({ ...form, price: v })} />
        <Field label="GST %" testId="prod-gst" type="number" value={form.gst_percent} onChange={(v) => setForm({ ...form, gst_percent: v })} />
        <Field label="HSN" testId="prod-hsn" value={form.hsn_code} onChange={(v) => setForm({ ...form, hsn_code: v })} />
        <div className="col-span-2 md:col-span-7 flex justify-end gap-2">
          {editingId && (
            <button type="button" onClick={cancelEdit} data-testid="prod-cancel" className="rounded-full border border-stone-300 text-stone-700 px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2">
              <X className="w-4 h-4" /> Cancel
            </button>
          )}
          <button disabled={saving} data-testid="prod-save" className="btn-primary rounded-full px-6 py-2.5 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editingId ? <Save className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {editingId ? "Update Product" : "Add Product"}
          </button>
        </div>
      </form>

      {loading ? (
        <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin text-stone-400 mx-auto" /></div>
      ) : items.length === 0 ? (
        <div className="text-center text-stone-500 py-16">
          <Package className="w-10 h-10 mx-auto text-stone-300 mb-3" strokeWidth={1.5} />
          <div>No products yet. Add your first SKU above.</div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50">
              <tr className="text-left text-xs uppercase tracking-widest text-stone-600">
                <th className="px-5 py-4">SKU</th>
                <th className="px-5 py-4">Name</th>
                <th className="px-5 py-4">Unit</th>
                <th className="px-5 py-4 text-right">Price (₹)</th>
                <th className="px-5 py-4 text-right">GST %</th>
                <th className="px-5 py-4">HSN</th>
                <th className="px-5 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} data-testid={`prod-row-${p.id}`} className="border-t border-stone-100 hover:bg-stone-50">
                  <td className="px-5 py-4 font-mono text-xs font-bold text-blue-700">{p.sku}</td>
                  <td className="px-5 py-4 font-semibold text-stone-900">{p.name}</td>
                  <td className="px-5 py-4 text-stone-600">{p.unit}</td>
                  <td className="px-5 py-4 text-right text-stone-800">₹{Number(p.price).toFixed(2)}</td>
                  <td className="px-5 py-4 text-right text-stone-600">{p.gst_percent}%</td>
                  <td className="px-5 py-4 text-stone-500 text-xs">{p.hsn_code || "—"}</td>
                  <td className="px-5 py-4 text-right whitespace-nowrap">
                    <button onClick={() => startEdit(p)} data-testid={`prod-edit-${p.id}`} className="text-blue-700 hover:text-blue-800 text-sm font-semibold inline-flex items-center gap-1 mr-4">
                      Edit
                    </button>
                    <button onClick={() => remove(p)} data-testid={`prod-delete-${p.id}`} className="text-rose-600 hover:text-rose-700 text-sm font-semibold inline-flex items-center gap-1">
                      <Trash2 className="w-4 h-4" /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const inputCls = "mt-1 w-full rounded-xl border border-stone-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-600 bg-white";
function Label({ children }) { return <label className="text-xs font-semibold uppercase tracking-widest text-stone-600">{children}</label>; }
function Field({ label, testId, value, onChange, type = "text" }) {
  return (
    <div>
      <Label>{label}</Label>
      <input data-testid={testId} type={type} value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} step={type === "number" ? "0.01" : undefined} />
    </div>
  );
}
